/* NOIRFORM theme core. Vanilla JS, no dependencies. */
(function () {
  'use strict';

  var NF = (window.NF = window.NF || {});
  var theme = window.theme || { routes: {}, strings: {} };
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
  function focusable(root) {
    return qsa('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', root)
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }
  NF.qs = qs; NF.qsa = qsa; NF.debounce = debounce;

  /* ---------- toast ---------- */
  var toastTimer;
  NF.toast = function (message, type) {
    var el = qs('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('toast--error', type === 'error');
    el.hidden = false;
    // force reflow so the transition runs
    void el.offsetWidth;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () { el.hidden = true; }, 300);
    }, 3200);
  };

  /* ---------- drawers ---------- */
  var activeDrawer = null;
  var lastTrigger = null;

  function setTriggers(id, expanded) {
    qsa('[data-drawer-open="' + id + '"]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  NF.drawer = {
    open: function (target, trigger) {
      var drawer = typeof target === 'string' ? document.getElementById(target) : target;
      if (!drawer) return;
      if (activeDrawer && activeDrawer !== drawer) NF.drawer.close(activeDrawer, true);
      lastTrigger = trigger || document.activeElement;
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
      document.documentElement.classList.add('has-drawer-open');
      setTriggers(drawer.id, true);
      activeDrawer = drawer;
      var focusTarget = qs('[data-drawer-focus]', drawer) || focusable(drawer)[0];
      setTimeout(function () { if (focusTarget) focusTarget.focus({ preventScroll: true }); }, reduceMotion ? 0 : 60);
      document.dispatchEvent(new CustomEvent('nf:drawer:open', { detail: { drawer: drawer } }));
    },
    close: function (target, keepLock) {
      var drawer = target || activeDrawer;
      if (typeof drawer === 'string') drawer = document.getElementById(drawer);
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('is-open');
      setTriggers(drawer.id, false);
      if (activeDrawer === drawer) activeDrawer = null;
      if (!keepLock) {
        document.documentElement.classList.remove('has-drawer-open');
        if (lastTrigger && document.contains(lastTrigger) && lastTrigger.focus) {
          try { lastTrigger.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        }
      }
      document.dispatchEvent(new CustomEvent('nf:drawer:close', { detail: { drawer: drawer } }));
    }
  };

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      NF.drawer.open(opener.getAttribute('data-drawer-open'), opener);
      return;
    }
    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      NF.drawer.close(closer.closest('[data-drawer]'));
      return;
    }
    // Cart link opens the drawer when the store uses drawer mode
    var cartLink = e.target.closest('[data-cart-open]');
    if (cartLink && theme.cartType === 'drawer') {
      var cartDrawer = document.getElementById('CartDrawer');
      if (cartDrawer) {
        e.preventDefault();
        NF.drawer.open(cartDrawer, cartLink);
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!activeDrawer) return;
    if (e.key === 'Escape') {
      NF.drawer.close(activeDrawer);
    } else if (e.key === 'Tab') {
      var items = focusable(activeDrawer);
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- header: height var, scrolled state, mega menu ---------- */
  function initHeader() {
    var header = qs('[data-header]');
    if (!header) return;
    var root = document.documentElement;

    function measure() {
      var rect = header.getBoundingClientRect();
      var bar = qs('.announcement');
      root.style.setProperty('--header-height', header.offsetHeight + 'px');
      root.style.setProperty('--header-bottom', Math.max(0, rect.bottom) + 'px');
      root.style.setProperty('--announcement-height', bar ? bar.offsetHeight + 'px' : '0px');
    }
    measure();
    window.addEventListener('resize', debounce(measure, 100));
    window.addEventListener('scroll', function () {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      measure();
    }, { passive: true });

    qsa('.primary-nav__item.has-children', header).forEach(function (item) {
      var link = qs('.primary-nav__link', item);
      function setOpen(open) { if (link) link.setAttribute('aria-expanded', open ? 'true' : 'false'); }
      item.addEventListener('mouseenter', function () { setOpen(true); item.classList.add('is-open'); });
      item.addEventListener('mouseleave', function () { setOpen(false); item.classList.remove('is-open'); });
      item.addEventListener('focusin', function () { setOpen(true); item.classList.add('is-open'); });
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) { setOpen(false); item.classList.remove('is-open'); }
      });
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { setOpen(false); item.classList.remove('is-open'); if (link) link.focus(); }
      });
    });
  }

  /* ---------- sliders (scroll-snap based) ---------- */
  function initSlider(root) {
    if (root.__nfSlider) return;
    var track = qs('[data-slider-track]', root);
    if (!track) return;
    root.__nfSlider = true;

    var scope = root.closest('section') || root;
    var prev = qs('[data-slider-prev]', scope);
    var next = qs('[data-slider-next]', scope);
    var dots = qsa('[data-slider-dot]', scope);
    var isHero = root.classList.contains('hero');
    var autoplay = root.getAttribute('data-autoplay') === 'true' && !reduceMotion;
    var interval = (parseFloat(root.getAttribute('data-interval')) || 5) * 1000;
    var timer = null;

    function slides() { return Array.prototype.slice.call(track.children).filter(function (c) { return !c.hidden; }); }
    function step() {
      var s = slides();
      if (s.length < 2) return s[0] ? s[0].getBoundingClientRect().width : track.clientWidth;
      return s[1].getBoundingClientRect().left - s[0].getBoundingClientRect().left;
    }
    function index() {
      var st = step();
      return st ? Math.round(track.scrollLeft / st) : 0;
    }
    function maxScroll() { return track.scrollWidth - track.clientWidth - 2; }
    function goTo(i) {
      var st = step();
      track.scrollTo({ left: i * st, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    function update() {
      var i = index();
      dots.forEach(function (d, n) {
        var active = n === i;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (!isHero) {
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= maxScroll();
      }
      slides().forEach(function (s, n) {
        if (isHero) s.setAttribute('aria-hidden', n === i ? 'false' : 'true');
      });
    }
    function go(dir) {
      var s = slides();
      if (isHero || autoplay) {
        var i = index() + dir;
        if (i >= s.length) i = 0;
        if (i < 0) i = s.length - 1;
        goTo(i);
      } else {
        track.scrollBy({ left: dir * step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    if (prev) prev.addEventListener('click', function () { go(-1); restart(); });
    if (next) next.addEventListener('click', function () { go(1); restart(); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () { goTo(parseInt(d.getAttribute('data-slider-dot'), 10) || 0); restart(); });
    });

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { update(); ticking = false; });
    }, { passive: true });
    window.addEventListener('resize', debounce(update, 120));

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { go(1); restart(); }
      if (e.key === 'ArrowLeft') { go(-1); restart(); }
    });

    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() {
      if (!autoplay || slides().length < 2) return;
      stop();
      timer = setInterval(function () { go(1); }, interval);
    }
    function restart() { if (autoplay) { stop(); start(); } }
    if (autoplay) {
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });
      start();
    }
    update();
  }

  /* ---------- footer wordmark fit ---------- */
  function fitText(box) {
    var inner = qs('[data-fit-inner]', box);
    if (!inner) return;
    inner.style.fontSize = '100px';
    var w = inner.scrollWidth;
    var target = box.clientWidth;
    if (!w || !target) return;
    // 2% slack: the negative tracking lets the last glyph overhang its measured box
    inner.style.fontSize = (98 * target / w) + 'px';
    box.style.height = inner.offsetHeight + 'px';
  }
  function initFit(root) {
    qsa('[data-fit-text]', root).forEach(function (box) {
      fitText(box);
      if ('ResizeObserver' in window && !box.__nfFit) {
        box.__nfFit = true;
        new ResizeObserver(debounce(function () { fitText(box); }, 60)).observe(box);
      }
    });
  }

  /* ---------- footer wordmark fill + back to top ---------- */
  function initWordmarkFill(root) {
    if (!('IntersectionObserver' in window)) return;
    qsa('.footer--card [data-fit-text]', root).forEach(function (box) {
      new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        box.classList.add('is-inview');
        obs.disconnect();
      }, { threshold: 0.6 }).observe(box);
    });
  }
  /* ---------- coverflow: phone carousels tilt and shrink the cards beside the centre one ---------- */
  function initCoverflow(root) {
    qsa('[data-coverflow]', root).forEach(function (list) {
      if (list.__nfCover) return;
      list.__nfCover = true;
      var items = qsa(':scope > li', list);
      if (items.length < 2) return;
      var frame = 0;
      function paint() {
        frame = 0;
        var scrolling = list.scrollWidth > list.clientWidth + 4;
        var mid = list.scrollLeft + list.clientWidth / 2;
        items.forEach(function (item) {
          if (!scrolling || reduceMotion) { item.style.transform = ''; item.style.zIndex = ''; return; }
          var d = (item.offsetLeft + item.offsetWidth / 2 - mid) / item.offsetWidth;
          d = Math.max(-2, Math.min(2, d));
          var a = Math.abs(d);
          item.style.transform = 'translateX(' + (-d * 22) + '%) rotateY(' + (-d * 16) + 'deg) scale(' + (1 - a * 0.14) + ')';
          item.style.zIndex = String(10 - Math.round(a * 4));
        });
      }
      function queue() { if (!frame) frame = requestAnimationFrame(paint); }
      list.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue);
      // open on the middle card so both sides show
      var start = items[Math.floor((items.length - 1) / 2)];
      if (list.scrollWidth > list.clientWidth + 4) {
        list.scrollLeft = start.offsetLeft + start.offsetWidth / 2 - list.clientWidth / 2;
      }
      paint();
    });
  }

  /* ---------- policy pages: "On this page" list built from the policy's own headings ---------- */
  function initPolicyToc() {
    var box = qs('.shopify-policy__container');
    var body = box && qs('.shopify-policy__body', box);
    if (!body || box.classList.contains('has-toc')) return;
    var heads = qsa('h2', body);
    if (heads.length < 3) return;
    var nav = document.createElement('nav');
    nav.className = 'policy-toc';
    nav.setAttribute('aria-label', 'On this page');
    var html = '<p class="policy-toc__heading">On this page</p><ol>';
    heads.forEach(function (h, i) {
      if (!h.id) h.id = 'policy-section-' + (i + 1);
      var text = h.textContent.trim().toLowerCase();
      html += '<li><a href="#' + h.id + '">' + text.charAt(0).toUpperCase() + text.slice(1) + '</a></li>';
    });
    nav.innerHTML = html + '</ol>';
    box.insertBefore(nav, body);
    box.classList.add('has-toc');
    if (!('IntersectionObserver' in window)) return;
    var links = qsa('a', nav);
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        links.forEach(function (a) { a.classList.toggle('is-current', a.getAttribute('href') === '#' + en.target.id); });
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    heads.forEach(function (h) { io.observe(h); });
  }

  /* ---------- footer 3D bag: drop in on view, hop when the cart count goes up ---------- */
  function initFooterBag(root) {
    qsa('[data-footer-bag]', root).forEach(function (bag) {
      if (bag.__nfBag) return;
      bag.__nfBag = true;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries, obs) {
          if (!entries[0].isIntersecting) return;
          bag.classList.add('is-inview');
          obs.disconnect();
        }, { threshold: 0.4 }).observe(bag);
      } else {
        bag.classList.add('is-inview');
      }
      var count = qs('[data-cart-count]', bag), stage = qs('.footer-bag__stage', bag);
      if (!count || !stage || !('MutationObserver' in window)) return;
      var last = parseInt(count.textContent, 10) || 0;
      new MutationObserver(function () {
        var now = parseInt(count.textContent, 10) || 0;
        if (now > last) {
          stage.classList.remove('is-bump');
          void stage.offsetWidth; // restart the hop
          stage.classList.add('is-bump');
        }
        last = now;
      }).observe(count, { childList: true, characterData: true, subtree: true });
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-back-to-top]')) return;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* ---------- mobile bottom bar: hide while scrolling down ---------- */
  (function () {
    var lastY = window.scrollY, ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var nav = qs('[data-bottom-nav]'), y = window.scrollY;
        if (nav && Math.abs(y - lastY) > 6) {
          nav.classList.toggle('is-hidden', y > lastY && y > 160);
          lastY = y;
        }
        ticking = false;
      });
    }, { passive: true });
  })();

  /* ---------- quantity steppers ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    var wrap = btn.closest('[data-qty]');
    var input = wrap && qs('[data-quantity-input]', wrap);
    if (!input) return;
    var min = input.min !== '' ? parseInt(input.min, 10) : 0;
    var val = parseInt(input.value, 10) || 0;
    val += btn.hasAttribute('data-qty-plus') ? 1 : -1;
    if (val < min) val = min;
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- quick add ---------- */
  function closeQuickAdds(except) {
    qsa('[data-quick-add].is-open').forEach(function (qa) {
      if (qa === except) return;
      qa.classList.remove('is-open');
      var t = qs('[data-quick-add-toggle]', qa);
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-quick-add-toggle]');
    if (toggle) {
      e.preventDefault();
      var qa = toggle.closest('[data-quick-add]');
      var open = !qa.classList.contains('is-open');
      closeQuickAdds(open ? qa : null);
      qa.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }
    var add = e.target.closest('[data-add-variant]');
    if (add) {
      e.preventDefault();
      if (add.disabled || !NF.cart) return;
      var wrapper = add.closest('[data-quick-add]');
      add.classList.add('is-loading');
      add.disabled = true;
      NF.cart.add({ id: add.getAttribute('data-add-variant'), quantity: 1 })
        .then(function () { if (wrapper) closeQuickAdds(null); })
        .catch(function (err) { NF.toast(err && err.message ? err.message : theme.strings.cartError, 'error'); })
        .then(function () { add.classList.remove('is-loading'); add.disabled = false; });
      return;
    }
    if (!e.target.closest('[data-quick-add]')) closeQuickAdds(null);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeQuickAdds(null);
  });

  /* ---------- predictive search ---------- */
  function initPredictive() {
    if (!theme.predictiveSearch) return;
    var form = qs('[data-predictive-form]');
    if (!form) return;
    var input = qs('[data-predictive-input]', form);
    var panel = form.closest('[data-search-panel]') || document;
    var results = qs('[data-predictive-results]', panel);
    if (!input || !results) return;
    var defaultHTML = results.innerHTML;
    var controller = null;

    function render(html) { results.innerHTML = html; }

    var run = debounce(function () {
      var term = input.value.trim();
      if (!term) {
        if (controller) controller.abort();
        render(defaultHTML);
        return;
      }
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      var url = theme.routes.predictiveSearch +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last' +
        '&section_id=predictive-search';
      results.classList.add('is-loading');
      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var payload = qs('[data-predictive-payload]', doc);
          render(payload ? payload.outerHTML : '<p class="predictive__empty">' + (theme.strings.noResults || '') + '</p>');
          results.classList.remove('is-loading');
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          results.classList.remove('is-loading');
        });
    }, 220);

    input.addEventListener('input', run);
    document.addEventListener('nf:drawer:close', function (e) {
      if (e.detail.drawer === panel) { input.value = ''; render(defaultHTML); }
    });
  }

  /* ---------- localization selects ---------- */
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-auto-submit]');
    if (sel && sel.form) sel.form.submit();
  });

  /* ---------- <product-recommendations> ---------- */
  if (window.customElements && !customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        var url = this.getAttribute('data-url');
        if (!url || this.querySelector('.container')) return;
        var self = this;
        var observer = new IntersectionObserver(function (entries, obs) {
          if (!entries[0].isIntersecting) return;
          obs.unobserve(self);
          fetch(url)
            .then(function (r) { return r.text(); })
            .then(function (text) {
              var doc = new DOMParser().parseFromString(text, 'text/html');
              var fresh = doc.querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim().length) {
                self.innerHTML = fresh.innerHTML;
              }
            })
            .catch(function () { /* recommendations are optional */ });
        }, { rootMargin: '0px 0px 300px 0px' });
        observer.observe(this);
      }
    });
  }

  /* ---------- init ---------- */
  function init(root) {
    qsa('[data-slider]', root).forEach(initSlider);
    initFit(root);
    initWordmarkFill(root);
    initFooterBag(root);
    initCoverflow(root);
    initPolicyToc();
  }

  function ready() {
    initHeader();
    initPredictive();
    init(document);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { initFit(document); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();

  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var slider = e.target.closest('[data-slider]');
    var track = slider && qs('[data-slider-track]', slider);
    if (track && e.target.parentNode === track) {
      track.scrollTo({ left: e.target.offsetLeft, behavior: 'auto' });
    }
  });
})();
